import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { MicProvider } from "./context/MicContext";
import { JournalProvider } from "./context/JournalContext";
import { InfoPointsProvider } from "./context/InfoPointsContext";
import { AudioClipsProvider } from "./context/AudioClipsContext";
import RequireAuth from "./components/RequireAuth";
import Header from "./components/Header";
import BottomNav from "./components/BottomNav";
import OfflineBanner from "./components/OfflineBanner";
import Home from "./pages/Home";
import Riepilogo from "./pages/Riepilogo";
import Profilo from "./pages/Profilo";
import Altro from "./pages/Altro";
import SensorDetail from "./pages/SensorDetail";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AddMattressWizard from "./pages/AddMattressWizard";
import Bedtime from "./pages/Bedtime";
import Statistiche from "./pages/Statistiche";
import Diario from "./pages/Diario";
import Obiettivi from "./pages/Obiettivi";
import AlarmRinging from "./pages/AlarmRinging";
import useAlarmNotificationListener from "./hooks/useAlarmNotificationListener";

// Admin
import { AdminAuthProvider } from "./admin/AdminAuthContext";
import AdminLogin from "./admin/AdminLogin";
import AdminLayout from "./admin/AdminLayout";
import RequireAdmin from "./admin/RequireAdmin";
import AdminDashboard from "./admin/pages/AdminDashboard";
import AdminUsers from "./admin/pages/AdminUsers";
import AdminUserDetail from "./admin/pages/AdminUserDetail";
import AdminAlgorithms from "./admin/pages/AdminAlgorithms";
import AdminScheduler from "./admin/pages/AdminScheduler";
import AdminQuality from "./admin/pages/AdminQuality";
import AdminSandbox from "./admin/pages/AdminSandbox";
import AdminAnalytics from "./admin/pages/AdminAnalytics";
import AdminConfig from "./admin/pages/AdminConfig";
import AdminBranding from "./admin/pages/AdminBranding";
import AdminInfoPoints from "./admin/pages/AdminInfoPoints";

function AppShell({ children }) {
  const location = useLocation();
  useAlarmNotificationListener();
  const isAdmin = location.pathname.startsWith("/admin");
  const isFullscreen =
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/forgot-password") ||
    location.pathname.startsWith("/reset-password") ||
    location.pathname.startsWith("/onboarding") ||
    location.pathname.startsWith("/bedtime") ||
    location.pathname.startsWith("/alarm-ringing");

  if (isAdmin) {
    // Admin layout renders its own structure
    return <>{children}</>;
  }

  if (isFullscreen) {
    return (
      <div className="min-h-screen w-full bg-[#1f2426] flex justify-center">
        <div className="w-full max-w-[430px] bg-[#2b3134] min-h-screen shadow-2xl overflow-hidden">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#1f2426] flex justify-center">
      <div className="w-full max-w-[430px] relative bg-[#2b3134] min-h-screen shadow-2xl overflow-hidden">
        <Header />
        <OfflineBanner />
        <main style={{ paddingTop: "calc(56px + env(safe-area-inset-top))" }}>
          {children}
        </main>
        <BottomNav />
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <MicProvider>
          <JournalProvider>
            <InfoPointsProvider>
            <AudioClipsProvider>
            <AdminAuthProvider>
            <BrowserRouter>
            <AppShell>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/onboarding/add-mattress"
                  element={
                    <RequireAuth>
                      <AddMattressWizard />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/bedtime"
                  element={
                    <RequireAuth>
                      <Bedtime />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/alarm-ringing"
                  element={
                    <RequireAuth>
                      <AlarmRinging />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/"
                  element={
                    <RequireAuth requireMattress>
                      <Home />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/riepilogo"
                  element={
                    <RequireAuth requireMattress>
                      <Riepilogo />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/statistiche"
                  element={
                    <RequireAuth requireMattress>
                      <Statistiche />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/diario"
                  element={
                    <RequireAuth>
                      <Diario />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/obiettivi"
                  element={
                    <RequireAuth>
                      <Obiettivi />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/profilo"
                  element={
                    <RequireAuth>
                      <Profilo />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/altro"
                  element={
                    <RequireAuth>
                      <Altro />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/sensor/:id"
                  element={
                    <RequireAuth requireMattress>
                      <SensorDetail />
                    </RequireAuth>
                  }
                />

                {/* -------- Admin backoffice -------- */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <RequireAdmin>
                      <AdminLayout />
                    </RequireAdmin>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="users/:id" element={<AdminUserDetail />} />
                  <Route path="algorithms" element={<AdminAlgorithms />} />
                  <Route path="scheduler" element={<AdminScheduler />} />
                  <Route path="quality" element={<AdminQuality />} />
                  <Route path="sandbox" element={<AdminSandbox />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="infopoints" element={<AdminInfoPoints />} />
                  <Route path="config" element={<AdminConfig />} />
                  <Route path="branding" element={<AdminBranding />} />
                </Route>
              </Routes>
            </AppShell>
          </BrowserRouter>
            </AdminAuthProvider>
            </AudioClipsProvider>
            </InfoPointsProvider>
          </JournalProvider>
        </MicProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
